class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
  }

  connectedCallback() {
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
    this.performRecommendations();
  }

  performRecommendations() {
    const recommendations = this.querySelector('[data-recommendations]');
    if (!recommendations || !this.dataset.url) return;

    const urls = [this.dataset.url];
    if (this.dataset.url.includes('intent=related')) {
      urls.push(this.dataset.url.replace('intent=related', 'intent=complementary'));
    }

    this.loadRecommendations(urls, recommendations);
  }

  loadRecommendations(urls, recommendations) {
    const url = urls.shift();
    if (!url) return;

    fetch(url)
      .then(response => response.text())
      .then(text => {
        const node = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-recommendations]');
        const recommendationsHTML = node ? node.innerHTML.trim() : '';
        if (!recommendationsHTML) {
          this.loadRecommendations(urls, recommendations);
          return;
        }
        this.classList.remove('hidden');
        recommendations.innerHTML = recommendationsHTML;
        this.initSlider();
      })
      .catch(() => this.loadRecommendations(urls, recommendations));
  }

  initSlider() {
    if (typeof Swiper === 'undefined') return;
    const element = this.querySelector('.swiper');
    if (!element) return;
    this.slider = new Swiper(element, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      touchStartPreventDefault: false,
      touchMoveStopPropagation: false
    });
  }

  onChange(event) {
    const card = event.target.closest('[data-recommendation-card]');
    if (!card) return;

    if (event.target.matches('[data-recommendation-color]')) {
      card.dataset.color = event.target.value;
      card.querySelectorAll('[data-recommendation-color]').forEach(input => {
        input.nextElementSibling?.classList.toggle('is-active', input.checked);
      });
      this.refreshSizes(card);
      this.updateCard(card);
      return;
    }

    if (!event.target.matches('[data-recommendation-size]')) return;
    card.querySelector('[data-recommendation-sizes]')?.classList.remove('is-required');
    card.querySelectorAll('[data-recommendation-size]').forEach(input => {
      input.nextElementSibling?.classList.toggle('is-active', input.checked);
    });
    this.updateCard(card);
  }

  onClick(event) {
    const button = event.target.closest('[data-recommendation-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const card = button.closest('[data-recommendation-card]');
    if (card.querySelector('[data-recommendation-size]') && !card.querySelector('[data-recommendation-size]:checked')) {
      const sizes = card.querySelector('[data-recommendation-sizes]');
      sizes?.classList.add('is-required');
      sizes?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
    this.addToCart(button);
  }

  cardVariants(card) {
    const data = card.querySelector('[data-recommendation-variants]');
    if (!data) return [];
    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      return [];
    }
  }

  matchingVariant(card) {
    const variants = this.cardVariants(card);
    const color = card.dataset.color || '';
    const size = card.querySelector('[data-recommendation-size]:checked')?.value || '';
    const matches = variant => (!color || variant.color === color) && (!size || variant.size === size);
    return variants.find(variant => matches(variant) && variant.available) || variants.find(matches);
  }

  refreshSizes(card) {
    const inputs = [...card.querySelectorAll('[data-recommendation-size]')];
    if (!inputs.length) return;

    const color = card.dataset.color || '';
    const variants = this.cardVariants(card);
    inputs.forEach(input => {
      const match = variants.find(variant => (!color || variant.color === color) && variant.size === input.value);
      input.disabled = !match?.available;
      if (input.disabled && input.checked) input.checked = false;
      input.nextElementSibling?.classList.toggle('is-active', input.checked);
    });
  }

  updateCard(card) {
    const variant = this.matchingVariant(card);
    if (!variant) return;
    const price = card.querySelector('[data-recommendation-price]');
    if (price) price.textContent = variant.price;
    const image = card.querySelector('img');
    if (image && variant.image) {
      image.src = variant.image;
      image.removeAttribute('srcset');
    }
    this.syncAddButton(card);
  }

  syncAddButton(card) {
    const button = card.querySelector('[data-recommendation-add]');
    if (!button) return;
    const needsSize = card.querySelector('[data-recommendation-size]') && !card.querySelector('[data-recommendation-size]:checked');
    if (needsSize) {
      button.textContent = button.dataset.labelChoose;
      button.classList.add('cart-recommendations__add--choose');
      button.style.backgroundColor = '';
      button.style.borderColor = '';
      button.style.color = '';
      return;
    }

    button.textContent = button.dataset.labelAdd;
    button.classList.remove('cart-recommendations__add--choose');
    this.paintAddButton(card, button);
  }

  paintAddButton(card, button) {
    const swatch = card.querySelector('input:checked + .product__swatch--color');
    if (!swatch) return;
    const background = getComputedStyle(swatch).backgroundColor;
    const match = background.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (!match || match[4] === '0') return;

    const red = Number(match[1]);
    const green = Number(match[2]);
    const blue = Number(match[3]);
    const text = this.contrastText(red, green, blue);
    const color = `rgb(${red}, ${green}, ${blue})`;
    button.style.backgroundColor = color;
    button.style.borderColor = color;
    button.style.color = text;
  }

  contrastText(red, green, blue) {
    const channel = value => {
      const scaled = value / 255;
      return scaled <= 0.03928 ? scaled / 12.92 : Math.pow((scaled + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue);
    return luminance > 0.45 ? '#111111' : '#ffffff';
  }

  addToCart(button) {
    const card = button.closest('[data-recommendation-card]');
    const checkedSize = card.querySelector('[data-recommendation-size]:checked');
    if (card.querySelector('[data-recommendation-size]') && !checkedSize) return;

    const variant = this.matchingVariant(card);
    if (!variant || !variant.available) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData();
    formData.append('id', variant.id);
    formData.append('quantity', '1');
    if (cartDrawer) {
      formData.append(
        'sections',
        cartDrawer.getSectionsToRender().map(section => section.section)
      );
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;
    button.disabled = true;

    fetch(`${routes.cart_add_url}`, config)
      .then(response => response.json())
      .then(response => {
        if (response.status) return;
        cartDrawer?.renderContents(response);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        button.disabled = false;
      });
  }
}

customElements.define('cart-recommendations', CartRecommendations);
